/**
 * "Aperçu 3D" of the poster editor, desktop and mobile (design handoff "Aperçu 3D cadres") : the
 * poster as it is made, hung at scale in a photographed room (shared.preview3d.RoomPreview), with
 * the finish, the size and the room to choose, and "Commander" (the editor's own order button).
 *
 * The finish and the size change the creation exactly as the finish and size screens do ; ordering
 * from here goes straight to the order, without the upsell screens asking again what was just
 * chosen (PosterMain, "order" with choicesMade).
 *
 * The picture is the page drawn without its frame (shared.preview3d.PosterArtwork), larger than
 * the cover thumbnail.
 * Opened by "wall-preview" (desktop PosterEditor, mobile PosterSettings → PosterMain).
 */
fx.ns("shared.popups", class WallPreviewPopup extends fx.components.Popup {

    constructor(model)
    {
        super(model);

        this.mobile = !fx.browser.isDesktop;
        this.showClose = false;
        this.closeOnClickBehind = false;

        if (!this.mobile)
        {
            this.contentWidth = window.innerWidth;
            this.contentHeight = window.innerHeight;
        }

        this.state = { room : 0, view : "face", measure : !this.mobile, tab : "finish" };
        this._onResizeHandler = this._onResize.bind(this);
    }

    // the drawing of the page, in pixels per millimetre : 1050 × 1500 for the 210 × 300 page
    get ARTWORK_RATIO() { return 5; }
    get CURTAIN() { return 160; }       // ms : the loader fades in over the room before it changes

    get FINISHES()
    {
        return [
            { code : "standard", label : fx.text("Poster seul"), short : fx.text("Poster"), border : 0 },
            // the moulding's width in cm on each side : 1 cm on the catalogue's frames, so a 20 × 30
            // print is a 22 × 32 frame on the wall, a 42 × 59 one 44 × 61
            { code : "wood", label : fx.text("Cadre bois"), short : fx.text("Bois"), border : 1 },
            { code : "black", label : fx.text("Cadre noir"), short : fx.text("Noir"), border : 1 },
            { code : "white", label : fx.text("Cadre blanc"), short : fx.text("Blanc"), border : 1 },
        ];
    }

    // the rooms' photos keep their names when they are redone : bump this so that no browser keeps
    // showing the old one
    get ROOMS_VERSION() { return "?v=7"; }

    /**
     * Photos 1408 × 768. cx / by : the frame's centre x and lowest bottom, % of the photo ; cy :
     * the height every size is centred at (RoomPreview.bottomOf : the 42 × 59 framed hangs where it
     * always did, the small sizes higher, off the furniture) ; pxPerCm : photo
     * pixels per cm, from the furniture.
     */
    get ROOMS()
    {
        let base = "/app/assets/images/wall-preview/";
        return [
            { id : "salon", label : fx.text("Salon"), alt : fx.text("Salon avec canapé rose"), cx : 50.8, by : 51, cy : 36.2, pxPerCm : 3.6 },
            { id : "chambre", label : fx.text("Chambre"), alt : fx.text("Chambre avec lit et commode"), cx : 49.9, by : 37, cy : 25.1, pxPerCm : 2.9 },
            { id : "cuisine", label : fx.text("Cuisine"), alt : fx.text("Cuisine au mur rose"), cx : 41.5, by : 62.5, cy : 42.0, pxPerCm : 5.0 },
        ].map((room) => Object.assign({
            image : base + room.id + ".jpg" + this.ROOMS_VERSION,
            thumb : base + room.id + "-thumb.jpg" + this.ROOMS_VERSION,
        }, room));
    }

    get creation()
    {
        return this.data.creation;
    }

    create()
    {
        super.create();

        this.view = new shared.preview3d.WallPreviewView({ mobile : this.mobile });
        this.addChild(this.view, { width : "100%", height : "100%", top : 0, left : 0 });
    }

    onReady()
    {
        super.onReady();

        window.addEventListener("resize", this._onResizeHandler);
        this.on("click", this._onClick, this.view.node);

        let scene = this.view.find(".wpScene");
        this.preview = new shared.preview3d.RoomPreview(scene, {
            mobile : this.mobile,
            band : () => this.view.band(),
        });

        if (!this.mobile)
        {
            this._onWheel = (e) => {
                e.preventDefault();
                this._setView(e.deltaY < 0 ? "close" : "face");
            };
            scene.addEventListener("wheel", this._onWheel, { passive : false });
        }

        this.view.fitScene();
        this._refresh();
        this.preview.measure(this.state.measure);

        this.view.loading(true);
        shared.preview3d.PosterArtwork.render(this, this.data, this.ARTWORK_RATIO)
            .then((url) => url ? this.preview.setArtwork(url) : null)
            .then(() => this.view.loading(false))
            .catch(() => this.view.loading(false));
    }

    destroy()
    {
        window.removeEventListener("resize", this._onResizeHandler);

        if (this._onWheel && this.view && this.view.node)
            this.view.find(".wpScene").removeEventListener("wheel", this._onWheel);

        if (this.preview)
            this.preview.destroy();

        this.preview = null;

        super.destroy();
    }

    // ---- the catalogue

    /**
     * The size in cm as it is sold : the format's description ("30 x 40 cm"), else its width and
     * height in mm. Formats come as portrait / landscape pairs, each with its own id.
     */
    _dimensions(format)
    {
        let found = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i.exec(format.description || format.dimensions || "");
        if (found)
            return { width : parseFloat(found[1].replace(",", ".")), height : parseFloat(found[2].replace(",", ".")) };

        if (format.width && format.height)
            return { width : format.width / 10, height : format.height / 10 };

        return { width : 40, height : 60 };
    }

    get _landscape()
    {
        return this.creation.shape_option == "landscape";
    }

    _oriented(size)
    {
        let short = Math.min(size.width, size.height);
        let long = Math.max(size.width, size.height);
        return this._landscape ? { width : long, height : short } : { width : short, height : long };
    }

    _price(creationOption)
    {
        let value = creationOption.price_base * fx.get("lang").tax;
        return value.toLocaleString(document.documentElement.lang || "fr", { minimumFractionDigits : 2, maximumFractionDigits : 2 }) + " €";
    }

    // every creation option of a finish, in the poster's orientation, smallest first
    _optionsOf(code)
    {
        let options = this.creation.creationOptions;
        let found = [];

        if (!options)
            return found;

        for (let i = 0; i < options.length; i++)
        {
            let option = options.getItemAt(i);
            if (!option.option || option.option.editor_code != code || !option.format || found.find((o) => o.formatid == option.formatid))
                continue;

            let size = this._dimensions(option.format);
            if ((size.width > size.height) != this._landscape && size.width != size.height)
                continue;

            found.push(option);
        }

        let area = (option) => { let size = this._dimensions(option.format); return size.width * size.height; };
        return found.sort((a, b) => area(a) - area(b));
    }

    get _finish()
    {
        let code = this.creation.option ? this.creation.option.editor_code : "standard";
        return this.FINISHES.find((finish) => finish.code == code) || this.FINISHES[0];
    }

    _product()
    {
        let size = this._oriented(this._dimensions(this.creation.format || {}));
        let finish = this._finish;
        return { width : size.width, height : size.height, border : finish.border, finish : finish.code };
    }

    // ---- state → screen

    _refresh()
    {
        let creation = this.creation;
        let finish = this._finish;
        let product = this._product();
        let rooms = this.ROOMS;
        let room = rooms[this.state.room];
        let price = creation.creationOption ? this._price(creation.creationOption) : "";
        let label = (cm) => (Math.round(cm * 10) / 10).toLocaleString(document.documentElement.lang || "fr");

        let finishes = this.FINISHES
            .filter((f) => !creation.creationOptions || this._optionsOf(f.code).length)
            .map((f) => ({ code : f.code, label : f.label, short : f.short, framed : f.border > 0, selected : f.code == finish.code }));

        let sizes = this._optionsOf(finish.code).map((option) => {
            let size = this._oriented(this._dimensions(option.format));
            return { id : option.formatid, width : size.width, height : size.height,
                     label : label(size.width) + " × " + label(size.height) + " cm",
                     price : this._price(option), selected : option.formatid == creation.formatid };
        });

        this.view.update({
            title : (finish.border ? fx.text("Poster encadré") : fx.text("Poster")) + " " + label(product.width) + " × " + label(product.height) + " cm",
            price : price,
            cart : fx.text("Commander"),
            finishLabel : finish.label,
            finishes : finishes,
            sizes : sizes,
            rooms : rooms.map((r, i) => ({ index : i, label : r.label, thumb : r.thumb, selected : i == this.state.room })),
            view : this.state.view,
            measure : this.state.measure,
            tab : this.state.tab,
        });

        this.preview.setRoom(room);
        this.preview.setProduct(product);
    }

    _setView(view)
    {
        if (this.state.view == view)
            return;

        this.state.view = view;
        this.preview.setView(view);
        this.view.update({ view : view });
    }

    // ---- actions

    _onClick(e)
    {
        let target = e.target && e.target.closest ? e.target.closest("[data-action]") : null;
        if (!target || target.disabled)
            return;

        let data = target.dataset;

        switch (data.action)
        {
            case "close":
                this.close();
                return;

            case "order":
                this.close();
                this.data.event("order", { choicesMade : true });
                return;

            case "view":
                this._setView(data.view);
                return;

            case "measure":
                this.state.measure = !this.state.measure;
                this.preview.measure(this.state.measure);
                this.view.update({ measure : this.state.measure });
                return;

            case "tab":
                this.state.tab = data.tab;
                this.view.update({ tab : data.tab });
                return;

            case "room":
                this._switchRoom(Number(data.room));
                return;

            case "finish":
                this._chooseFinish(data.finish);
                break;

            case "size":
                this.creation.updateFormat(Number(data.format));
                break;

            default:
                return;
        }

        this._refresh();
    }

    /**
     * Another room : the loader fades in over the scene, the room changes under it — the photo
     * loading, the poster jumping to its spot — and the loader fades out once the photo is in,
     * rather than showing every step of it. The card is selected at once.
     */
    _switchRoom(index)
    {
        if (index == this.state.room || !this.ROOMS[index])
            return;

        this.state.room = index;
        this.view.update({ rooms : this.ROOMS.map((r, i) => ({ index : i, label : r.label, thumb : r.thumb, selected : i == index })) });

        let token = this._roomSwitch = {};
        this.view.loading(true);

        // the loader painted and faded in first : two frames, then its fade — a fixed delay alone
        // could run out before a busy page has painted it, and the switch would show
        let painted = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        painted().then(() => this.delay(() => {
            if (this._roomSwitch != token || !this.preview)
                return;

            this._refresh();
            this.preview.setRoom(this.ROOMS[index]).then(() => {
                if (this._roomSwitch == token && this.view)
                    this.view.loading(false);
            });
        }, this.CURTAIN));
    }

    /**
     * The finish in the current size ; a frame asked for in a size it is not made in (59 × 84)
     * comes in the biggest one it is.
     */
    _chooseFinish(code)
    {
        let creation = this.creation;
        let options = this._optionsOf(code);
        if (!options.length)
            return;

        let option = options.find((o) => o.formatid == creation.formatid) || options[options.length - 1];

        if (option.formatid != creation.formatid)
            creation.formatid = option.formatid;

        creation.updateOption(option.option_id);
    }

    _onResize()
    {
        if (!this.mobile)
        {
            this.contentWidth = window.innerWidth;
            this.contentHeight = window.innerHeight;
        }

        this.delay(() => {
            if (this.view && this.preview)
            {
                this.view.fitScene();
                this.preview.layout();
            }
        }, 50);
    }
});
